import { useMemo, useCallback } from 'react';
import {
  getAllRowIds, buildSelectionMap, getBaseColumnId, buildColumnSelections,
} from './tableFieldSelectionUseCase';

/**
 * Hook to manage cell selection state for TableField
 * @param {object} params
 * @param {FormTableData} params.tableData
 * @param {FormTableSelection[]} params.value
 * @param {(selections: FormTableSelection[]) => void} params.onChange
 * @returns {{ isSelected: (rowId: string, colId: string) => boolean, isBaseColumn: (colId: string) => boolean, handleCellClick: (rowId: string, colId: string) => void, handleColumnSelect: (colId: string) => void }}
 */
export function useTableFieldSelection({ tableData, value, onChange }) {
  const allRowIds = useMemo(() => getAllRowIds(tableData), [tableData]);

  const selectionMap = useMemo(() => buildSelectionMap(value), [value]);

  const isSelected = useCallback(
    (/** @type {string} */ rowId, /** @type {string} */ colId) => selectionMap[rowId] === colId,
    [selectionMap],
  );

  const isBaseColumn = useCallback(
    (/** @type {string} */ colId) => getBaseColumnId(value) === colId,
    [value],
  );

  const handleCellClick = useCallback((/** @type {string} */ rowId, /** @type {string} */ colId) => {
    if (selectionMap[rowId] === colId) return;

    const next = value.filter((s) => s.rowId !== rowId);
    next.push({ rowId, colId });
    onChange(next);
  }, [value, selectionMap, onChange]);

  const handleColumnSelect = useCallback((/** @type {string} */ colId) => {
    if (getBaseColumnId(value) === colId) return;

    onChange(buildColumnSelections(allRowIds, colId));
  }, [allRowIds, value, onChange]);

  return {
    isSelected, isBaseColumn, handleCellClick, handleColumnSelect,
  };
}
